import pAll from 'p-all';
import omit from 'lodash/omit';
import get from 'lodash/get';
import find from 'lodash/find';
import pickBy from 'lodash/pickBy';
import difference from 'lodash/difference';
import union from 'lodash/union';
import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';

import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { FULFILLERS, MERCHANTS } from '../../constants/transactorType';
import {
  ALL,
  getNotificationType,
  getResourceTypes,
  INDIVIDUAL,
  NONE,
  PRODUCT_NOTIFICATION
} from '../../constants/subscriptionConstants';
import { getCounterType, typeIsFulfiller, typeIsMerchant } from '../identities/utils';
import * as subscriptionsApi from '../../services/Subscriptions';
import { notificationsHubAuthorizer } from '../../utils/auth';
import {
  createApiErrorNotificationAction,
  createApiLoadNotificationAction,
  createFinishApiLoadNotificationAction
} from '../utils';
import {
  parseProductSubscription,
  parseSubscriptions,
  subscribeNotificationMsg,
  unsubscribeNotificationMsg
} from './utils';

// ACTION TYPES

const CHECK_NOTIFICATION_AUTHORIZATION_REQUEST = 'CHECK_NOTIFICATION_AUTHORIZATION_REQUEST';
const CHECK_NOTIFICATION_AUTHORIZATION_FAILURE = 'CHECK_NOTIFICATION_AUTHORIZATION_FAILURE';
const CHECK_NOTIFICATION_AUTHORIZATION_SUCCESS = 'CHECK_NOTIFICATION_AUTHORIZATION_SUCCESS';

const AUTHORIZE_NOTIFICATION_REQUEST = 'AUTHORIZE_NOTIFICATION_REQUEST';
const AUTHORIZE_NOTIFICATION_FAILURE = 'AUTHORIZE_NOTIFICATION_FAILURE';
const AUTHORIZE_NOTIFICATION_SUCCESS = 'AUTHORIZE_NOTIFICATION_SUCCESS';

const FETCH_ALL_SUBSCRIPTIONS_REQUEST = 'FETCH_ALL_SUBSCRIPTIONS_REQUEST';
const FETCH_ALL_SUBSCRIPTIONS_FAILURE = 'FETCH_ALL_SUBSCRIPTIONS_FAILURE';
const FETCH_ALL_SUBSCRIPTIONS_SUCCESS = 'FETCH_ALL_SUBSCRIPTIONS_SUCCESS';

const NOTIFICATIONS_SUBSCRIBE_REQUEST = 'NOTIFICATIONS_SUBSCRIBE_REQUEST';
const NOTIFICATIONS_SUBSCRIBE_FAILURE = 'NOTIFICATIONS_SUBSCRIBE_FAILURE';
const NOTIFICATIONS_SUBSCRIBE_SUCCESS = 'NOTIFICATIONS_SUBSCRIBE_SUCCESS';

const NOTIFICATIONS_UNSUBSCRIBE_REQUEST = 'NOTIFICATIONS_UNSUBSCRIBE_REQUEST';
const NOTIFICATIONS_UNSUBSCRIBE_FAILURE = 'NOTIFICATIONS_UNSUBSCRIBE_FAILURE';
const NOTIFICATIONS_UNSUBSCRIBE_SUCCESS = 'NOTIFICATIONS_UNSUBSCRIBE_SUCCESS';

const CONFIGURE_MULTIPLE_IDENTITIES_REQUEST = 'CONFIGURE_MULTIPLE_IDENTITIES_REQUEST';
const CONFIGURE_MULTIPLE_IDENTITIES_FAILURE = 'CONFIGURE_MULTIPLE_IDENTITIES_FAILURE';
const CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS = 'CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS';

// ACTION CREATORS
const checkNotificationAuthorizationRequest = () => ({ type: CHECK_NOTIFICATION_AUTHORIZATION_REQUEST });
const checkNotificationAuthorizationFailure = payload => ({ type: CHECK_NOTIFICATION_AUTHORIZATION_FAILURE, payload });
const checkNotificationAuthorizationSuccess = payload => ({ type: CHECK_NOTIFICATION_AUTHORIZATION_SUCCESS, payload });

const authorizeNotificationRequest = () => ({ type: AUTHORIZE_NOTIFICATION_REQUEST });
const authorizeNotificationFailure = payload => ({ type: AUTHORIZE_NOTIFICATION_FAILURE, payload });
const authorizeNotificationSuccess = payload => ({ type: AUTHORIZE_NOTIFICATION_SUCCESS, payload });

const fetchAllSubscriptionsRequest = () => ({ type: FETCH_ALL_SUBSCRIPTIONS_REQUEST });
const fetchAllSubscriptionsFailure = payload => ({ type: FETCH_ALL_SUBSCRIPTIONS_FAILURE, payload });
const fetchAllSubscriptionsSuccess = payload => ({ type: FETCH_ALL_SUBSCRIPTIONS_SUCCESS, payload });

const notificationsSubscribeRequest = () => ({ type: NOTIFICATIONS_SUBSCRIBE_REQUEST });
const notificationsSubscribeFailure = payload => ({ type: NOTIFICATIONS_SUBSCRIBE_FAILURE, payload });
const notificationsSubscribeSuccess = payload => ({ type: NOTIFICATIONS_SUBSCRIBE_SUCCESS, payload });

const notificationsUnsubscribeRequest = () => ({ type: NOTIFICATIONS_UNSUBSCRIBE_REQUEST });
const notificationsUnsubscribeFailure = payload => ({ type: NOTIFICATIONS_UNSUBSCRIBE_FAILURE, payload });
const notificationsUnsubscribeSuccess = payload => ({ type: NOTIFICATIONS_UNSUBSCRIBE_SUCCESS, payload });

const configureMultipleIdentitiesRequest = () => ({ type: CONFIGURE_MULTIPLE_IDENTITIES_REQUEST });
const configureMultipleIdentitiesFailure = payload => ({ type: CONFIGURE_MULTIPLE_IDENTITIES_FAILURE, payload });
const configureMultipleIdentitiesSuccess = payload => ({ type: CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS, payload });

const checkNotificationAuthorization = () => dispatch => {
  dispatch(checkNotificationAuthorizationRequest());

  notificationsHubAuthorizer
    .didUserAuthorize()
    .then(response => dispatch(checkNotificationAuthorizationSuccess(response)))
    .catch(err => {
      dispatch(checkNotificationAuthorizationFailure(err));
      dispatch(createApiErrorNotificationAction(err));
    });
};

const authorizeNotification = returnUri => dispatch => {
  dispatch(authorizeNotificationRequest());

  notificationsHubAuthorizer
    .ensureAuthorized({ returnUri })
    .then(response => dispatch(authorizeNotificationSuccess(response)))
    .catch(err => {
      dispatch(authorizeNotificationFailure(err));
      dispatch(createApiErrorNotificationAction(err));
    });
};

const fetchAllSubscriptions = resourceType => dispatch => {
  dispatch(fetchAllSubscriptionsRequest());

  subscriptionsApi
    .fetchAllSubscriptions(resourceType)
    .then(response => {
      const { subscriptions } = response;
      dispatch(fetchAllSubscriptionsSuccess(subscriptions));
    })
    .catch(err => {
      dispatch(fetchAllSubscriptionsFailure(err));
      dispatch(createApiErrorNotificationAction(err));
    });
};

const subscribeToNotifications = (
  previousSubscriptionIds,
  subscriptionType,
  subscriberId,
  subscriberType,
  notificationType,
  counterParties = null
) => dispatch => {
  dispatch(notificationsSubscribeRequest());

  const { loading, success, failure } = subscribeNotificationMsg;

  let subscribingNotification = dispatch(createApiLoadNotificationAction(loading.title, loading.message));

  const removePreviousSubs = subscriptionsApi.unsubscribe(previousSubscriptionIds, notificationType);
  const resourceTypes = getResourceTypes(notificationType);
  let makeNewSubscriptions = [];

  resourceTypes.forEach(resourceType => {
    makeNewSubscriptions.push(
      subscriptionsApi.subscribe(subscriptionType, subscriberId, subscriberType, counterParties, resourceType)
    );
  });

  Promise.all([removePreviousSubs, ...makeNewSubscriptions])
    .then(([previousSubsResponse, ...newSubscriptionsResponse]) => {
      dispatch(
        notificationsSubscribeSuccess({
          subscriptionType,
          subscriberId,
          subscriberType,
          counterParties,
          response: newSubscriptionsResponse.flat(),
          deletedSubscriptionIds: previousSubscriptionIds
        })
      );
      dispatch(
        createFinishApiLoadNotificationAction({
          title: success.title,
          notificationHandle: subscribingNotification,
          message: success.message,
          isSuccess: true,
          successButtons: null
        })
      );
    })
    .catch(err => {
      dispatch(notificationsSubscribeFailure(err));
      dispatch(
        createFinishApiLoadNotificationAction({
          notificationHandle: subscribingNotification,
          title: failure.title,
          message: failure.message,
          isSuccess: false,
          notSuccessButtons: null,
          errObject: err
        })
      );
    });
};

const unsubscribeFromNotifications = (
  subscriptionIds,
  unsubscriberId,
  unsubscriberType,
  notificationType
) => dispatch => {
  dispatch(notificationsUnsubscribeRequest());
  const { loading, success, failure } = unsubscribeNotificationMsg;

  let unsubscribingNotification = dispatch(createApiLoadNotificationAction(loading.title, loading.message));

  subscriptionsApi
    .unsubscribe(subscriptionIds, notificationType)
    .then(() => {
      dispatch(
        notificationsUnsubscribeSuccess({ unsubscriberId, unsubscriberType, deletedSubscriptionIds: subscriptionIds })
      );
      dispatch(
        createFinishApiLoadNotificationAction({
          title: success.title,
          notificationHandle: unsubscribingNotification,
          message: success.message,
          isSuccess: true,
          successButtons: null
        })
      );
    })
    .catch(err => {
      dispatch(notificationsUnsubscribeFailure(err));
      dispatch(
        createFinishApiLoadNotificationAction({
          notificationHandle: unsubscribingNotification,
          title: failure.title,
          message: failure.message,
          isSuccess: false,
          notSuccessButtons: null,
          errObject: err
        })
      );
    });
};

const getCurrentSubscriptionIds = (identityIds, identityType, state) => {
  let configMap = {};
  const { subscriptions } = state;

  if (typeIsFulfiller(identityType)) {
    configMap = subscriptions.byFulfillerId || {};
  }

  if (typeIsMerchant(identityType)) {
    configMap = subscriptions.byMerchantId || {};
  }

  return identityIds.reduce((arr, id) => {
    const subs = configMap[id];
    const { subscriptionType, counterParties } = subs || {};

    switch (subscriptionType) {
      case ALL:
        arr.push(counterParties.subscriptionId);
        break;
      case INDIVIDUAL:
        counterParties.forEach(({ subscriptionId }) => arr.push(subscriptionId));
        break;
      default:
        break;
    }

    return arr;
  }, []);
};

// TODO: do testing to figure out the best value for rate limiting
const configureMultipleIdentities = (
  subscriptionType,
  identityType,
  identityIds,
  notificationType,
  counterPartyIds = null,
  rateLimit = 5
) => (dispatch, getState) => {
  dispatch(configureMultipleIdentitiesRequest());
  const currentSubscriptionIds =
    notificationType === PRODUCT_NOTIFICATION
      ? compact(identityIds.reduce((arr, id) => arr.concat(getProductSubscriptionIdsByMerchant(getState())[id]), []))
      : getCurrentSubscriptionIds(identityIds, identityType, getState());

  const removePreviousSubs = subscriptionsApi.unsubscribe(currentSubscriptionIds, notificationType);
  const { loading, success, failure } =
    subscriptionType === NONE ? unsubscribeNotificationMsg : subscribeNotificationMsg;

  let configureMultipleNotification = dispatch(createApiLoadNotificationAction(loading.title, loading.message));

  switch (subscriptionType) {
    case NONE:
      removePreviousSubs
        .then(response => {
          dispatch(
            configureMultipleIdentitiesSuccess({
              subscriptionType,
              identityIds,
              identityType,
              counterPartyIds,
              response,
              deletedSubscriptionIds: currentSubscriptionIds
            })
          );
          dispatch(
            createFinishApiLoadNotificationAction({
              title: success.title,
              notificationHandle: configureMultipleNotification,
              message: success.message,
              isSuccess: true,
              successButtons: null
            })
          );
        })
        .catch(err => {
          dispatch(configureMultipleIdentitiesFailure(err));
          dispatch(
            createFinishApiLoadNotificationAction({
              notificationHandle: configureMultipleNotification,
              title: failure.title,
              message: failure.message,
              isSuccess: false,
              notSuccessButtons: null,
              errObject: err
            })
          );
        });
      return;

    case ALL:
    case INDIVIDUAL:
      const resourceTypes = getResourceTypes(notificationType);

      const createSubs = flatMap(identityIds, id =>
        resourceTypes.map(resourceType =>
          subscriptionsApi.subscribe(subscriptionType, id, identityType, counterPartyIds, resourceType)
        )
      );
      const createAllSubs = createSubs.map(sub => () => sub);
      const subscribeCalls = [...createAllSubs, () => removePreviousSubs];
      pAll(subscribeCalls, { concurrency: rateLimit })
        .then(response => {
          dispatch(
            configureMultipleIdentitiesSuccess({
              subscriptionType,
              identityIds,
              identityType,
              counterPartyIds,
              response,
              deletedSubscriptionIds: currentSubscriptionIds
            })
          );
          dispatch(
            createFinishApiLoadNotificationAction({
              title: success.title,
              notificationHandle: configureMultipleNotification,
              message: success.message,
              isSuccess: true,
              successButtons: null
            })
          );
        })
        .catch(err => {
          dispatch(configureMultipleIdentitiesFailure(err));
          dispatch(
            createFinishApiLoadNotificationAction({
              notificationHandle: configureMultipleNotification,
              title: failure.title,
              message: failure.message,
              isSuccess: false,
              notSuccessButtons: null,
              errObject: err
            })
          );
        });
      return;

    default:
      return;
  }
};

export const actions = {
  checkNotificationAuthorization,
  authorizeNotification,
  fetchAllSubscriptions,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  configureMultipleIdentities
};

// REDUCERS
function bySubscriptionId(state = {}, action) {
  switch (action.type) {
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS: {
      const subscriptions = action.payload.reduce((map, sub) => {
        map[sub.id] = sub;
        return map;
      }, {});
      return { ...state, ...subscriptions };
    }

    case NOTIFICATIONS_SUBSCRIBE_SUCCESS: {
      const { deletedSubscriptionIds, response } = action.payload;
      const updatedSubs = pickBy(state, sub => !deletedSubscriptionIds.includes(sub.id));

      const subscriptions = response.reduce((map, sub) => {
        map[sub.id] = sub;
        return map;
      }, {});

      return { ...updatedSubs, ...subscriptions };
    }

    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS: {
      const { deletedSubscriptionIds } = action.payload;
      return pickBy(state, sub => !deletedSubscriptionIds.includes(sub.id));
    }

    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS: {
      const { deletedSubscriptionIds, response } = action.payload;

      const updatedSubs = pickBy(state, sub => !deletedSubscriptionIds.includes(sub.id));

      const subsResponse = compact(response.flat());
      const subscriptions = subsResponse.reduce((map, sub) => {
        map[sub.id] = sub;
        return map;
      }, {});

      return { ...updatedSubs, ...subscriptions };
    }

    default:
      return state;
  }
}

function productSubscriptionIdsByMerchant(state = {}, action) {
  const mapSubs = (subscriptions, state, subsToDelete = []) => {
    return subscriptions.reduce((map, sub) => {
      if (
        getNotificationType(sub.resourceType) !== PRODUCT_NOTIFICATION ||
        get(sub, 'metadata.subscriber.type') !== MERCHANTS
      ) {
        return map;
      }
      const merchantId = get(sub, 'metadata.subscriber.id');
      map[merchantId] ? (map[merchantId] = union(map[merchantId], [sub.id])) : (map[merchantId] = [sub.id]);
      map[merchantId] = difference(map[merchantId], subsToDelete);

      return map;
    }, state);
  };

  switch (action.type) {
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS:
      return mapSubs(action.payload, state);

    case NOTIFICATIONS_SUBSCRIBE_SUCCESS:
      const { response, deletedSubscriptionIds } = action.payload;
      return mapSubs(response, state, deletedSubscriptionIds);

    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS: {
      const { unsubscriberId, deletedSubscriptionIds } = action.payload;
      const subscriptionsByMerchant = state[unsubscriberId];

      const updatedSubs = difference(subscriptionsByMerchant, deletedSubscriptionIds);
      return { ...state, [unsubscriberId]: updatedSubs };
    }

    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS: {
      const { deletedSubscriptionIds, response } = action.payload;
      const updatedSubs = Object.keys(state).reduce((map, identityId) => {
        map[identityId] = difference(map[identityId], deletedSubscriptionIds);

        return map;
      }, state);

      return mapSubs(compact(response.flat()), updatedSubs);
    }

    default:
      return state;
  }
}

function byFulfillerId(state = {}, action) {
  switch (action.type) {
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS:
      return parseSubscriptions(action.payload).fulfillers;

    case NOTIFICATIONS_SUBSCRIBE_SUCCESS:
      if (typeIsFulfiller(action.payload.subscriberType)) {
        const { subscriptionType, subscriberId, subscriberType, counterParties, response } = action.payload;
        const counterPartyType = getCounterType(subscriberType);

        switch (subscriptionType) {
          case ALL:
            const allCounterPartiesObject = { counterPartyType, counterPartyId: ALL, subscriptionId: response[0].id };
            return { ...state, [subscriberId]: { subscriptionType, counterParties: allCounterPartiesObject } };
          case INDIVIDUAL:
            const subscribeIndividualObject = response.map((res, idx) => ({
              counterPartyType,
              counterPartyId: counterParties[idx],
              subscriptionId: res.id
            }));
            return { ...state, [subscriberId]: { subscriptionType, counterParties: subscribeIndividualObject } };
          case NONE:
            return state;
          default:
            return state;
        }
      }
      return state;

    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS:
      const { unsubscriberId, unsubscriberType } = action.payload;
      return typeIsFulfiller(unsubscriberType) ? omit(state, unsubscriberId) : state;

    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS:
      if (typeIsFulfiller(action.payload.identityType)) {
        const { subscriptionType, identityIds, identityType, counterPartyIds, response } = action.payload;
        const newSubsArray = response.slice(0, -1);
        const counterPartyType = getCounterType(identityType);

        switch (subscriptionType) {
          case NONE:
            return omit(state, identityIds);

          case ALL:
            const subscribeAllMapping = newSubsArray.map(({ id }, idx) => ({
              identityId: [identityIds[idx]],
              subscriptionId: id
            }));

            const newSubscribeAllMapping = subscribeAllMapping.reduce((map, { identityId, subscriptionId }) => {
              map[identityId] = {
                subscriptionType: ALL,
                counterParties: { counterPartyType, counterPartyId: ALL, subscriptionId }
              };
              return map;
            }, {});

            return { ...state, ...newSubscribeAllMapping };

          case INDIVIDUAL:
            const individualMapping = newSubsArray.map((arr, idx) => {
              const identityId = identityIds[idx];
              const cp = arr.map(({ id }, idx) => ({
                counterPartyType,
                counterPartyId: counterPartyIds[idx],
                subscriptionId: id
              }));
              return { identityId, counterParties: cp };
            });

            const newSubscribeIndividualMapping = individualMapping.reduce((map, { identityId, counterParties }) => {
              map[identityId] = { subscriptionType: INDIVIDUAL, counterParties };
              return map;
            }, {});

            return { ...state, ...newSubscribeIndividualMapping };

          default:
            return state;
        }
      }
      return state;

    default:
      return state;
  }
}

function byMerchantId(state = {}, action) {
  switch (action.type) {
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS:
      return parseSubscriptions(action.payload).merchants;

    case NOTIFICATIONS_SUBSCRIBE_SUCCESS:
      if (typeIsMerchant(action.payload.subscriberType)) {
        const { subscriptionType, subscriberId, subscriberType, counterParties, response } = action.payload;
        const counterPartyType = getCounterType(subscriberType);

        switch (subscriptionType) {
          case ALL:
            const allCounterPartiesObject = { counterPartyType, counterPartyId: ALL, subscriptionId: response[0].id };
            return { ...state, [subscriberId]: { subscriptionType, counterParties: allCounterPartiesObject } };
          case INDIVIDUAL:
            const subscribeIndividualObject = response.map((res, idx) => ({
              counterPartyType,
              counterPartyId: counterParties[idx],
              subscriptionId: res.id
            }));
            return { ...state, [subscriberId]: { subscriptionType, counterParties: subscribeIndividualObject } };
          case NONE:
            return state;
          default:
            return state;
        }
      }
      return state;

    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS:
      const { unsubscriberId, unsubscriberType } = action.payload;
      return typeIsMerchant(unsubscriberType) ? omit(state, unsubscriberId) : state;

    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS:
      if (typeIsMerchant(action.payload.identityType)) {
        const { subscriptionType, identityIds, identityType, counterPartyIds, response } = action.payload;
        const newSubsArray = response.slice(0, -1);
        const counterPartyType = getCounterType(identityType);

        switch (subscriptionType) {
          case NONE:
            return omit(state, identityIds);

          case ALL:
            const subscribeAllMapping = newSubsArray.map(({ id }, idx) => ({
              identityId: [identityIds[idx]],
              subscriptionId: id
            }));

            const newSubscribeAllMapping = subscribeAllMapping.reduce((map, { identityId, subscriptionId }) => {
              map[identityId] = {
                subscriptionType: ALL,
                counterParties: { counterPartyType, counterPartyId: ALL, subscriptionId }
              };
              return map;
            }, {});

            return { ...state, ...newSubscribeAllMapping };

          case INDIVIDUAL:
            const individualMapping = newSubsArray.map((arr, idx) => {
              const identityId = identityIds[idx];
              const cp = arr.map(({ id }, idx) => ({
                counterPartyType,
                counterPartyId: counterPartyIds[idx],
                subscriptionId: id
              }));
              return { identityId, counterParties: cp };
            });

            const newSubscribeIndividualMapping = individualMapping.reduce((map, { identityId, counterParties }) => {
              map[identityId] = { subscriptionType: INDIVIDUAL, counterParties };
              return map;
            }, {});

            return { ...state, ...newSubscribeIndividualMapping };

          default:
            return state;
        }
      }
      return state;

    default:
      return state;
  }
}

function isLoading(state = {}, action) {
  switch (action.type) {
    case CHECK_NOTIFICATION_AUTHORIZATION_REQUEST:
      return { ...state, CHECK_NOTIFICATION_AUTHORIZATION: true };
    case CHECK_NOTIFICATION_AUTHORIZATION_FAILURE:
    case CHECK_NOTIFICATION_AUTHORIZATION_SUCCESS:
      return { ...state, CHECK_NOTIFICATION_AUTHORIZATION: false };
    case AUTHORIZE_NOTIFICATION_REQUEST:
      return { ...state, AUTHORIZE_NOTIFICATION: true };
    case AUTHORIZE_NOTIFICATION_FAILURE:
    case AUTHORIZE_NOTIFICATION_SUCCESS:
      return { ...state, AUTHORIZE_NOTIFICATION: false };
    case FETCH_ALL_SUBSCRIPTIONS_REQUEST:
      return { ...state, FETCH_ALL_SUBSCRIPTIONS: true };
    case FETCH_ALL_SUBSCRIPTIONS_FAILURE:
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS:
      return { ...state, FETCH_ALL_SUBSCRIPTIONS: false };
    case NOTIFICATIONS_SUBSCRIBE_REQUEST:
      return { ...state, NOTIFICATIONS_SUBSCRIBE: true };
    case NOTIFICATIONS_SUBSCRIBE_FAILURE:
    case NOTIFICATIONS_SUBSCRIBE_SUCCESS:
      return { ...state, NOTIFICATIONS_SUBSCRIBE: false };
    case NOTIFICATIONS_UNSUBSCRIBE_REQUEST:
      return { ...state, NOTIFICATIONS_UNSUBSCRIBE: true };
    case NOTIFICATIONS_UNSUBSCRIBE_FAILURE:
    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS:
      return { ...state, NOTIFICATIONS_UNSUBSCRIBE: false };
    case CONFIGURE_MULTIPLE_IDENTITIES_REQUEST:
      return { ...state, CONFIGURE_MULTIPLE_IDENTITIES: true };
    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS:
    case CONFIGURE_MULTIPLE_IDENTITIES_FAILURE:
      return { ...state, CONFIGURE_MULTIPLE_IDENTITIES: false };
    default:
      return state;
  }
}

function isLoaded(state = {}, action) {
  switch (action.type) {
    case CHECK_NOTIFICATION_AUTHORIZATION_REQUEST:
    case CHECK_NOTIFICATION_AUTHORIZATION_FAILURE:
      return { ...state, CHECK_NOTIFICATION_AUTHORIZATION: false };
    case CHECK_NOTIFICATION_AUTHORIZATION_SUCCESS:
      return { ...state, CHECK_NOTIFICATION_AUTHORIZATION: true, AUTHORIZE_NOTIFICATION: action.payload };
    case AUTHORIZE_NOTIFICATION_REQUEST:
    case AUTHORIZE_NOTIFICATION_FAILURE:
      return { ...state, AUTHORIZE_NOTIFICATION: false };
    case AUTHORIZE_NOTIFICATION_SUCCESS:
      return { ...state, AUTHORIZE_NOTIFICATION: true };
    case FETCH_ALL_SUBSCRIPTIONS_REQUEST:
    case FETCH_ALL_SUBSCRIPTIONS_FAILURE:
      return { ...state, FETCH_ALL_SUBSCRIPTIONS: false };
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS:
      return { ...state, FETCH_ALL_SUBSCRIPTIONS: true };
    case NOTIFICATIONS_SUBSCRIBE_REQUEST:
    case NOTIFICATIONS_SUBSCRIBE_FAILURE:
      return { ...state, NOTIFICATIONS_SUBSCRIBE: false };
    case NOTIFICATIONS_SUBSCRIBE_SUCCESS:
      return { ...state, NOTIFICATIONS_SUBSCRIBE: true };
    case NOTIFICATIONS_UNSUBSCRIBE_REQUEST:
    case NOTIFICATIONS_UNSUBSCRIBE_FAILURE:
      return { ...state, NOTIFICATIONS_UNSUBSCRIBE: false };
    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS:
      return { ...state, NOTIFICATIONS_UNSUBSCRIBE: true };
    case CONFIGURE_MULTIPLE_IDENTITIES_REQUEST:
    case CONFIGURE_MULTIPLE_IDENTITIES_FAILURE:
      return { ...state, CONFIGURE_MULTIPLE_IDENTITIES: false };
    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS:
      return { ...state, CONFIGURE_MULTIPLE_IDENTITIES: true };
    default:
      return state;
  }
}

function hasError(state = {}, action) {
  switch (action.type) {
    case CHECK_NOTIFICATION_AUTHORIZATION_REQUEST:
    case CHECK_NOTIFICATION_AUTHORIZATION_SUCCESS:
      return { ...state, CHECK_NOTIFICATION_AUTHORIZATION: false };
    case CHECK_NOTIFICATION_AUTHORIZATION_FAILURE:
      return { ...state, CHECK_NOTIFICATION_AUTHORIZATION: true };
    case AUTHORIZE_NOTIFICATION_REQUEST:
    case AUTHORIZE_NOTIFICATION_SUCCESS:
      return { ...state, AUTHORIZE_NOTIFICATION: false };
    case AUTHORIZE_NOTIFICATION_FAILURE:
      return { ...state, AUTHORIZE_NOTIFICATION: true };
    case FETCH_ALL_SUBSCRIPTIONS_REQUEST:
    case FETCH_ALL_SUBSCRIPTIONS_SUCCESS:
      return { ...state, FETCH_ALL_SUBSCRIPTIONS: false };
    case FETCH_ALL_SUBSCRIPTIONS_FAILURE:
      return { ...state, FETCH_ALL_SUBSCRIPTIONS: true };
    case NOTIFICATIONS_SUBSCRIBE_REQUEST:
    case NOTIFICATIONS_SUBSCRIBE_SUCCESS:
      return { ...state, NOTIFICATIONS_SUBSCRIBE: false };
    case NOTIFICATIONS_SUBSCRIBE_FAILURE:
      return { ...state, NOTIFICATIONS_SUBSCRIBE: true };
    case NOTIFICATIONS_UNSUBSCRIBE_REQUEST:
    case NOTIFICATIONS_UNSUBSCRIBE_SUCCESS:
      return { ...state, NOTIFICATIONS_UNSUBSCRIBE: false };
    case NOTIFICATIONS_UNSUBSCRIBE_FAILURE:
      return { ...state, NOTIFICATIONS_UNSUBSCRIBE: true };
    case CONFIGURE_MULTIPLE_IDENTITIES_REQUEST:
    case CONFIGURE_MULTIPLE_IDENTITIES_SUCCESS:
      return { ...state, CONFIGURE_MULTIPLE_IDENTITIES: false };
    case CONFIGURE_MULTIPLE_IDENTITIES_FAILURE:
      return { ...state, CONFIGURE_MULTIPLE_IDENTITIES: true };
    default:
      return state;
  }
}

const subscriptionsReducer = combineReducers({
  bySubscriptionId,
  productSubscriptionIdsByMerchant,
  byFulfillerId,
  byMerchantId,
  isLoading,
  isLoaded,
  hasError
});

export default subscriptionsReducer;

// SELECTORS
const getFulfillerSubscriptions = state => state.subscriptions.byFulfillerId;
const getMerchantSubscriptions = state => state.subscriptions.byMerchantId;

const getIsCheckingAuthorization = state => state.subscriptions.isLoading.CHECK_NOTIFICATION_AUTHORIZATION;
const getHasCheckingAuthorizationFailed = state => state.subscriptions.hasError.CHECK_NOTIFICATION_AUTHORIZATION;

const getIsAuthorizing = state => state.subscriptions.isLoading.AUTHORIZE_NOTIFICATION;
const getIsAuthorized = state => state.subscriptions.isLoaded.AUTHORIZE_NOTIFICATION;
const getHasAuthorizationFailed = state => state.subscriptions.hasError.AUTHORIZE_NOTIFICATION;

const getIsFetchingAllSubscriptions = state => state.subscriptions.isLoading.FETCH_ALL_SUBSCRIPTIONS;
const getHasFetchingAllSubscriptionsFailed = state => state.subscriptions.hasError.FETCH_ALL_SUBSCRIPTIONS;

const getSubscriptionIds = state => state.subscriptions.bySubscriptionId;
const getProductSubscriptionIdsByMerchant = state => state.subscriptions.productSubscriptionIdsByMerchant;

const getIsAuthLoading = createSelector(
  [getIsCheckingAuthorization, getIsAuthorizing],
  (isCheckingAuthorization, isAuthorizing) => {
    return isCheckingAuthorization || isAuthorizing || false;
  }
);

const getHasAuthError = createSelector(
  [getHasCheckingAuthorizationFailed, getHasAuthorizationFailed],
  (hasCheckingAuthorizationFailed, hasAuthorizationFailed) => {
    return hasCheckingAuthorizationFailed || hasAuthorizationFailed || false;
  }
);

const getAvailableSubscriptions = createSelector(
  getFulfillerSubscriptions,
  getMerchantSubscriptions,
  (state, identityType) => ({ identityType }),
  (fulfillerSubscriptions, merchantSubscriptions, { identityType }) => {
    switch (identityType) {
      case FULFILLERS:
        return fulfillerSubscriptions;
      case MERCHANTS:
        return merchantSubscriptions;
      default:
        return [];
    }
  }
);

const getProductSubscriptions = createSelector(
  [getSubscriptionIds, getProductSubscriptionIdsByMerchant],
  (subscriptionIds, productSubscriptionIdsByMerchant) => {
    return Object.keys(productSubscriptionIdsByMerchant).reduce((map, merchantId) => {
      map[merchantId] = productSubscriptionIdsByMerchant[merchantId].map(id => subscriptionIds[id]);
      return map;
    }, {});
  }
);

const getGroupedProductSubscriptions = createSelector(getProductSubscriptions, productSubscriptions => {
  return Object.keys(productSubscriptions).reduce((map, merchantId) => {
    const subscriptions = productSubscriptions[merchantId];
    subscriptions.forEach(subscription => {
      const parsedSub = parseProductSubscription(subscription);
      const { subscriptionId, sellerType, sellerId } = parsedSub;
      const subscriptionType = sellerType && sellerId ? INDIVIDUAL : ALL;

      if (!map[merchantId]) {
        map[merchantId] = {
          subscriptionType: subscriptionType,
          counterParties: []
        };
      }

      const counterParty = find(map[merchantId].counterParties, party => party.counterPartyId === sellerId);
      if (counterParty) {
        counterParty.subscriptionIds.push(subscriptionId);
      } else {
        map[merchantId].counterParties.push({
          counterPartyType: FULFILLERS,
          counterPartyId: sellerId ? sellerId : ALL,
          subscriptionIds: [subscriptionId]
        });
      }
    });

    return map;
  }, {});
});

export const selectors = {
  getIsAuthLoading,
  getIsAuthorized,
  getHasAuthError,
  getIsFetchingAllSubscriptions,
  getHasFetchingAllSubscriptionsFailed,
  getAvailableSubscriptions,
  getGroupedProductSubscriptions
};
